import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Chip,
  Tooltip,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../services/api';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import clienteService from '../services/clienteService';

interface Cliente {
  nroCliente: number;
  nombre: string;
  razonSocial?: string;
  cuit?: string;
  direccion?: string;
  telefono1?: string;
  email?: string;
  tipoIva?: string;
  vendedor?: number;
}

interface ClientePagedResult {
  items: Cliente[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const Clientes: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [clientesData, setClientesData] = useState<ClientePagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openImportar, setOpenImportar] = useState(false);
  const [importPreviewRows, setImportPreviewRows] = useState<any[]>([]);
  const [importFileName, setImportFileName] = useState<string>('');
  const [isProcessingImport, setIsProcessingImport] = useState(false);
  const [importResult, setImportResult] = useState<{
    total: number;
    exitosos: number;
    conErrores: number;
    mensajesError: string[];
  } | null>(null);
  const [importProgress, setImportProgress] = useState<{ procesados: number; total: number } | null>(null);

  const fetchClientes = async (pageNumber: number, pageSize: number, search?: string) => {
    const rawSearch = (search ?? '').trim();
    const compact = rawSearch.replace(/\s+/g, '');
    const isNumericSearch = compact !== '' && /^\d+$/.test(compact);
    if (isNumericSearch) {
      try {
        const nro = Number(compact);
        const cliente = await clienteService.getCliente(nro);
        const adjustedData = {
          items: cliente ? [cliente] : [],
          totalCount: cliente ? 1 : 0,
          pageNumber: 0,
          pageSize,
          totalPages: 1
        };
        setClientesData(adjustedData);
        return;
      } catch (error: any) {
        if (error?.response?.status === 404) {
          setClientesData({ items: [], totalCount: 0, pageNumber: 0, pageSize, totalPages: 0 });
          return;
        }
        console.error('Error al buscar por nroCliente:', error);
        setClientesData({ items: [], totalCount: 0, pageNumber: 0, pageSize, totalPages: 0 });
        return;
      }
    }
    try {
      const response = await api.get('/api/clientes', {
        params: { pageNumber: pageNumber + 1, pageSize, searchTerm: rawSearch }
      });
      const adjustedData = {
        ...response.data,
        pageNumber: response.data.pageNumber - 1
      };
      setClientesData(adjustedData);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchClientes(page, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };
  const handleDelete = async (nroCliente: number) => {
    if (window.confirm('¿Está seguro de eliminar este cliente?')) {
      try {
        await api.delete(`/api/clientes/${nroCliente}`);
        fetchClientes(page, rowsPerPage, searchTerm);
      } catch (error) {
        console.error('Error al eliminar cliente:', error);
      }
    }
  };

  const handleDescargarPlantilla = () => {
    try {
      const headers = [
        'nroCliente', 'nombre', 'codigoDireccion', 'razonSocial', 'direccion', 'codigoCiudad', 'telefono1', 'telefono2', 'email', 'cuit', 'tipoIva', 'condVta', 'credito', 'nroLista', 'contacto', 'fechaAlta', 'fechaBaja', 'tipoCliente', 'ctaMaestra', 'nroCuenta', 'deposito', 'nroClienteExterno', 'vendedor',
        'codigoRuta', 'porcDesc', 'porcDesc2', 'porcDesc3'
      ];
      const data = [headers, new Array(headers.length).fill('')];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
      const instrucciones = [
        ['Columna', 'Descripción', 'Valores aceptados'],
        ['nroCliente', 'Número de cliente', 'Número único (obligatorio)'],
        ['nombre', 'Nombre del cliente', 'Texto (obligatorio)'],
        ['codigoDireccion', 'Código de dirección', 'Texto (opcional). Si viene en blanco se usará "FISCAL" como código por defecto'],
        ['razonSocial', 'Razón social', 'Texto (opcional)'],
        ['direccion', 'Dirección', 'Texto (opcional)'],
        ['codigoCiudad', 'Código de ciudad', 'Número (obligatorio). Debe existir en el maestro de ciudades'],
        ['telefono1', 'Teléfono principal', 'Texto (opcional)'],
        ['telefono2', 'Teléfono secundario', 'Texto (opcional)'],
        ['email', 'Email', 'Email válido (obligatorio)'],
        ['cuit', 'CUIT', 'Texto (opcional)'],
        ['tipoIva', 'Tipo IVA', 'I=Inscripto, M=Monotributo, E=Exento, C=Consumidor (opcional)'],
        ['condVta', 'Condición de venta', 'Texto (opcional)'],
        ['credito', 'Crédito', 'Número (opcional)'],
        ['nroLista', 'Número de lista', 'Número (opcional)'],
        ['contacto', 'Persona de contacto', 'Texto (opcional)'],
        ['fechaAlta', 'Fecha de alta', 'Formato fecha (opcional)'],
        ['fechaBaja', 'Fecha de baja', 'Formato fecha (opcional)'],
        ['tipoCliente', 'Tipo de cliente', 'Texto (opcional, si se omite se toma ACTIVO)'],
        ['ctaMaestra', 'Cuenta maestra', 'Número (opcional)'],
        ['nroCuenta', 'Cuenta contable', 'Número (obligatorio para la API; debe existir en el plan de cuentas)'],
        ['deposito', 'Depósito', 'Número (opcional)'],
        ['codigoRuta', 'Ruta / Zona (código)', 'Número (opcional). Corresponde al código de ruta/zona definido en el sistema'],
        ['nroClienteExterno', 'Número cliente externo', 'Texto (opcional)'],
        ['vendedor', 'Vendedor', 'Número (opcional)'],
        ['porcDesc', 'Porcentaje descuento 1', 'Número (opcional)'],
        ['porcDesc2', 'Porcentaje descuento 2', 'Número (opcional)'],
        ['porcDesc3', 'Porcentaje descuento 3', 'Número (opcional)'],
      ];
      const wsInstrucciones = XLSX.utils.aoa_to_sheet(instrucciones);
      XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');
      const fileName = 'PlantillaClientes.xlsx';
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar('✅ Plantilla Excel descargada correctamente', { variant: 'success', autoHideDuration: 4000 });
    } catch (error) {
      enqueueSnackbar('❌ Error al crear la plantilla Excel', { variant: 'error', autoHideDuration: 4000 });
    }
  };

  const getTipoIvaChip = (tipoIva?: string) => {
    if (!tipoIva) {
      return {
        label: 'Sin dato',
        bgColor: '#9e9e9e',
        textColor: '#ffffff'
      };
    }

    const normalized = tipoIva.toUpperCase();

    if (normalized === 'I' || normalized.includes('RESPONSABLE')) {
      return {
        label: 'Resp. Insc.',
        bgColor: '#1976d2',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'M' || normalized.includes('MONOTRIBUTO')) {
      return {
        label: 'Monotributo',
        bgColor: '#4caf50',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'E' || normalized.includes('EXENTO')) {
      return {
        label: 'Exento',
        bgColor: '#ff9800',
        textColor: '#ffffff'
      };
    }

    if (normalized === 'C' || normalized.includes('CONSUMIDOR')) {
      return {
        label: 'Cons. Final',
        bgColor: '#9c27b0',
        textColor: '#ffffff'
      };
    }

    return {
      label: tipoIva,
      bgColor: '#757575',
      textColor: '#ffffff'
    };
  };


  const onImportChangeClientes = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fileBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(fileBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });
      if (!rows.length) {
        setImportPreviewRows([]);
        setImportFileName('');
        setImportResult(null);
        return;
      }
      console.log("Cabeceras detectadas por XLSX:", Object.keys(rows[0]));
      setImportPreviewRows(rows);
      setImportFileName(file.name);
      setImportResult(null);
      // Vista previa lista; el usuario continuará con "Procesar"
    } catch (error) {
      setImportPreviewRows([]);
      setImportFileName('');
      setImportResult(null);
      // Error al leer archivo; la vista previa queda vacía
    }
  };

  const handleProcesarImportacionClientes = async () => {
    if (!importPreviewRows.length) return;
    try {
      setIsProcessingImport(true);
      setImportProgress(null);
      const rows = importPreviewRows;
      const camposRequeridosApi = ['nroCliente', 'nombre', 'codigoCiudad'];
      const camposApi = [
        'nroCliente', 'nombre', 'codigoDireccion', 'razonSocial', 'direccion', 'codigoCiudad', 'telefono1', 'telefono2', 'email', 'cuit', 'tipoIva', 'condVta', 'credito', 'nroLista', 'contacto', 'fechaAlta', 'fechaBaja', 'tipoCliente', 'ctaMaestra', 'nroCuenta', 'deposito', 'nroClienteExterno', 'vendedor', 'codigoRuta', 'porcDesc', 'porcDesc2', 'porcDesc3'
      ];
      const camposNumericosApi = ['nroCliente','codigoCiudad','credito','ctaMaestra','nroCuenta','deposito','vendedor','codigoRuta','porcDesc','porcDesc2','porcDesc3'];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      const headers = Object.keys(rows[0]);
      const headersLower = headers.map(h => String(h).toLowerCase().trim());
      const noEncontrados = camposApi.filter(h => !headersLower.includes(h.toLowerCase()));
      if (noEncontrados.length > 0) {
        setImportResult({
          total: rows.length,
          exitosos: 0,
          conErrores: rows.length,
          mensajesError: [`Columnas faltantes en el archivo: ${noEncontrados.join(', ')}`],
        });
        return;
      }

      let fallos = 0;
      const mensajesError: string[] = [];
      const validPayloads: { payload: any; rowIndex: number }[] = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowNormalizado: Record<string, any> = {};
        Object.keys(row).forEach((key) => {
          rowNormalizado[key.toLowerCase().trim()] = row[key];
        });
        console.log(`Fila completa fila ${i+2}:`, rowNormalizado);

        let valido = true;
        for (const campo of camposRequeridosApi) {
          const lower = campo.toLowerCase();
          let valor = rowNormalizado[lower];
          if (campo === 'nroCliente') {
            if (typeof valor === 'string') {
              valor = valor.replace(/[,]/g, '.').replace(/ /g, '');
            }
            console.log(`Valor real nroCliente fila ${i+2}:`, valor, typeof valor, 'Número convertido:', Number(valor));
          }
          if (campo === 'codigoCiudad') {
            // Ciudad requerida: no permitir vacío ni 0
            const num = Number(valor);
            if (
              valor === undefined || valor === null || String(valor).toString().trim() === '' ||
              isNaN(num) || num === 0
            ) {
              const msg = `Fila ${i+2}: El campo Ciudad es obligatorio y debe ser un código de ciudad válido.`;
              mensajesError.push(msg);
              fallos++;
              valido = false;
              break;
            }
            continue;
          }

          if (
            valor === undefined || valor === null || String(valor) === '' ||
            (typeof valor === 'string' && valor.trim().toLowerCase() === 'string') ||
            (campo === 'nroCliente' && (isNaN(Number(valor)) || Number(valor) === 0))
          ) {
            const msg = `Fila ${i+2}: El campo obligatorio "${campo}" está vacío o mal completado.`;
            mensajesError.push(msg);
            fallos++;
            valido = false;
            break;
          }
        }
        if (!valido) continue;

        for (const campoNum of camposNumericosApi) {
          const lower = campoNum.toLowerCase();
          let raw = rowNormalizado[lower];
          if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
            if (typeof raw === 'string') {
              raw = raw.replace(/[,]/g, '.').replace(/ /g, '');
            }
            if (isNaN(Number(raw))) {
              const msg = `Fila ${i+2}: La columna "${campoNum}" debe ser numérica.`;
              mensajesError.push(msg);
              fallos++;
              valido = false;
              break;
            }
          }
        }
        if (!valido) continue;

        const rawEmail = rowNormalizado['email'];
        const emailStr = String(rawEmail ?? '').trim();
        if (emailStr && !emailRegex.test(emailStr)) {
          const msg = `Fila ${i+2}: El email "${emailStr}" tiene formato inválido. Escriba un correo válido (ej: nombre@dominio.com).`;
          mensajesError.push(msg);
          fallos++;
          continue;
        }

        const cliente: any = {};
        camposApi.forEach((key) => {
          const lower = key.toLowerCase();
          let valor = rowNormalizado[lower];
          if (key === 'nroCliente' && typeof valor === 'string') {
            valor = valor.replace(/[,]/g, '.').replace(/ /g, '');
            cliente[key] = valor === '' ? null : Number(valor);
          } else if (camposNumericosApi.includes(key)) {
            cliente[key] = (valor !== undefined && valor !== null && String(valor).trim() !== '' && !isNaN(Number(valor))) ? Number(valor) : null;
          } else if (key === 'cuit') {
            cliente[key] = (valor !== undefined && valor !== null && valor !== '' ? String(valor) : null);
          } else if (key === 'codigoDireccion') {
            const texto = String(valor ?? '').trim();
            cliente[key] = texto === '' ? 'FISCAL' : texto;
          } else if (key === 'telefono1' || key === 'telefono2') {
            cliente[key] = (valor === undefined || valor === null || String(valor).trim() === '')
              ? null
              : String(valor).trim();
          } else if (key === 'tipoCliente') {
            const texto = String(valor ?? '').trim();
            cliente[key] = texto === '' ? null : texto;
          } else if (["fechaAlta","fechaBaja"].includes(key)) {
            cliente[key] = (typeof valor === 'string' && valor.match(/^\d{4}-\d{2}-\d{2}/)) ? (new Date(valor + 'T00:00:00Z')).toISOString() : (valor ? (isNaN(new Date(valor).getTime()) ? null : new Date(valor).toISOString()) : null);
          } else if (key === 'email') {
            cliente[key] = (valor === undefined || valor === '') ? null : String(valor).trim().toLowerCase();
          } else if (key === 'razonSocial' || key === 'direccion' || key === 'contacto' || key === 'nroLista' || key === 'nroClienteExterno') {
            cliente[key] = (valor === undefined || valor === null || valor === '') ? null : String(valor).trim();
          } else {
            cliente[key] = valor === undefined || valor === '' ? null : (typeof valor === 'string' ? valor : String(valor ?? ''));
          }
        });

        // Valores por defecto equivalentes al ABM cuando vienen vacíos en la plantilla
        if (!cliente.condVta || String(cliente.condVta).trim() === '') {
          cliente.condVta = 'EFECTIVO';
        }
        if (!cliente.tipoCliente || String(cliente.tipoCliente).trim() === '') {
          cliente.tipoCliente = 'A'; // Activo
        }
        if (cliente.vendedor === null || cliente.vendedor === undefined || Number(cliente.vendedor) === 0) {
          cliente.vendedor = 1;
        }
        if (cliente.nroCobrador === null || cliente.nroCobrador === undefined || Number(cliente.nroCobrador) === 0) {
          cliente.nroCobrador = 1;
        }
        if (cliente.codigoRuta === null || cliente.codigoRuta === undefined || Number(cliente.codigoRuta) === 0) {
          cliente.codigoRuta = 1;
        }
        if (!cliente.fechaAlta) {
          const hoy = new Date();
          cliente.fechaAlta = hoy.toISOString();
        }

        if (
          cliente.nombre === undefined || cliente.nombre === null ||
          typeof cliente.nombre !== 'string' ||
          cliente.nombre.trim() === '' ||
          cliente.nombre.trim().toLowerCase() === 'string'
        ) {
          const msg = `Fila ${i+2}: El campo obligatorio "nombre" está vacío, mal completado o tiene texto "string".`;
          mensajesError.push(msg);
          fallos++;
          continue;
        }

        if (cliente.deposito === '' || cliente.deposito === undefined || cliente.deposito === null || isNaN(Number(cliente.deposito))) cliente.deposito = 0;
        if (cliente.ctaMaestra !== null && isNaN(Number(cliente.ctaMaestra))) cliente.ctaMaestra = null;
        if (cliente.vendedor !== null && isNaN(Number(cliente.vendedor))) cliente.vendedor = null;
        if (cliente.credito !== null && isNaN(Number(cliente.credito))) cliente.credito = null;
        if (cliente.codigoCiudad !== null && isNaN(Number(cliente.codigoCiudad))) cliente.codigoCiudad = null;

        const clientePayload: any = { ...cliente };
        clientePayload.deposito = Number(clientePayload.deposito ?? 0);
        clientePayload.nroCliente = Number(clientePayload.nroCliente);
        // Asegurar que campos de texto sean string (Excel puede devolver números)
        ['nombre', 'razonSocial', 'direccion', 'telefono1', 'telefono2', 'email', 'cuit', 'codigoDireccion', 'contacto', 'nroLista', 'nroClienteExterno', 'condVta', 'tipoIva', 'tipoCliente'].forEach((k) => {
          const v = clientePayload[k];
          if (v != null && typeof v !== 'string') clientePayload[k] = String(v);
        });
        validPayloads.push({ payload: clientePayload, rowIndex: i + 2 });
      }

      if (validPayloads.length === 0) {
        setImportResult({ total: rows.length, exitosos: 0, conErrores: fallos, mensajesError });
        return;
      }

      setImportProgress({ procesados: 0, total: validPayloads.length });
      let exitos = 0;
      const batchSize = 100;

      for (let start = 0; start < validPayloads.length; start += batchSize) {
        const chunk = validPayloads.slice(start, start + batchSize);
        const payloads = chunk.map((x) => x.payload);
        try {
          const res = await clienteService.createClientesBulk(payloads);
          exitos += res.exitosos;
          fallos += res.fallidos;
          res.errores.forEach((e) => {
            const rowIndex = chunk[e.indice]?.rowIndex ?? start + e.indice + 2;
            mensajesError.push(`Fila ${rowIndex} (cliente ${e.nroCliente}): ${e.mensaje}`);
          });
        } catch (err: any) {
          const data = err?.response?.data;
          const _status = err?.response?.status;
          let msg = data?.message || err?.message || 'Error al procesar el lote';
          if (data?.errors) {
            const errs = data.errors;
            if (typeof errs === 'object') {
              Object.keys(errs).forEach((k) => {
                const arr = Array.isArray(errs[k]) ? errs[k] : [errs[k]];
                arr.forEach((m: string) => {
                  mensajesError.push(`Lote filas ${chunk[0]?.rowIndex ?? start + 2}-${chunk[chunk.length - 1]?.rowIndex ?? start + batchSize + 1}: ${k} - ${m}`);
                });
              });
            }
          } else {
            mensajesError.push(`Lote filas ${chunk[0]?.rowIndex ?? start + 2}-${chunk[chunk.length - 1]?.rowIndex ?? start + batchSize + 1}: ${msg}`);
          }
          fallos += chunk.length;
        }
        setImportProgress({ procesados: Math.min(start + batchSize, validPayloads.length), total: validPayloads.length });
      }

      setImportResult({
        total: rows.length,
        exitosos: exitos,
        conErrores: fallos,
        mensajesError,
      });
      fetchClientes(page, rowsPerPage, searchTerm);
    } finally {
      setIsProcessingImport(false);
      setImportProgress(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Clientes
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/ventas/clientes/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25,118,210,0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25,118,210,0.4)',
                  }
                }}
              >
                {isMobile ? 'Nuevo' : 'Nuevo Cliente'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => setOpenImportar(true)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                }}
              >
                Importar clientes
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, CUIT o N° de cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {clientesData.items.map((cliente) => {
                const tipoIvaChip = getTipoIvaChip(cliente.tipoIva);
                return (
                  <Card
                    key={cliente.nroCliente}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {cliente.nombre}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              N° Cliente: {cliente.nroCliente}
                            </Typography>
                          </Box>
                          <Chip
                            label={tipoIvaChip.label}
                            size="small"
                            sx={{
                              bgcolor: tipoIvaChip.bgColor,
                              color: tipoIvaChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                            }}
                          />
                        </Stack>
                        {cliente.razonSocial && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Razón Social:</strong> {cliente.razonSocial}
                          </Typography>
                        )}
                        {cliente.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {cliente.cuit}
                          </Typography>
                        )}
                        {cliente.direccion && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Dirección:</strong> {cliente.direccion}
                          </Typography>
                        )}
                        {cliente.telefono1 && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Teléfono:</strong> {cliente.telefono1}
                          </Typography>
                        )}
                        {cliente.email && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Email:</strong> {cliente.email}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Tooltip title="Editar cliente">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/ventas/clientes/${encodeURIComponent(cliente.nroCliente)}`)}
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar cliente">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(cliente.nroCliente)}
                              sx={{
                                bgcolor: '#f44336',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#d32f2f' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {clientesData.items.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay clientes para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={clientesData.totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </Stack>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>N° Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Razón Social</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Dirección</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Teléfono</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo IVA</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientesData.items.map((cliente) => {
                    const tipoIvaChip = getTipoIvaChip(cliente.tipoIva);
                    return (
                      <TableRow
                        key={cliente.nroCliente}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{cliente.nroCliente}</TableCell>
                        <TableCell>{cliente.nombre}</TableCell>
                        <TableCell>{cliente.razonSocial || '-'}</TableCell>
                        <TableCell>{cliente.cuit || '-'}</TableCell>
                        <TableCell>{cliente.direccion || '-'}</TableCell>
                        <TableCell>{cliente.telefono1 || '-'}</TableCell>
                        <TableCell>{cliente.email || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={tipoIvaChip.label}
                            size="small"
                            sx={{
                              bgcolor: tipoIvaChip.bgColor,
                              color: tipoIvaChip.textColor,
                              fontWeight: 500,
                              borderRadius: 1.5,
                              minWidth: 100,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Editar cliente">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/ventas/clientes/${encodeURIComponent(cliente.nroCliente)}`)}
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    bgcolor: '#1976d2',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar cliente">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(cliente.nroCliente)}
                                sx={{
                                  bgcolor: '#f44336',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    bgcolor: '#d32f2f',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {clientesData.items.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay clientes para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={clientesData.totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
      {/* Modal de importación */}
      <Dialog open={openImportar} onClose={() => setOpenImportar(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap:1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Clientes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            {/* Bloque 1: Descargar plantilla */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>Descarga la plantilla Excel con el formato correcto para importar clientes.</Typography>
              <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* Bloque 2: Importar desde archivo */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudUploadIcon color="success" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selecciona tu archivo Excel completado para importar los clientes.
              </Typography>
              <label htmlFor="excel-upload-clientes">
                <input
                  id="excel-upload-clientes"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={onImportChangeClientes}
                />
                <Button variant="contained" fullWidth color="success" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                  Seleccionar Archivo
                </Button>
              </label>
              {importFileName && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon sx={{ color: 'success.main', fontSize: 28 }} />
                    <Typography variant="body2" fontWeight={600}>
                      {importFileName}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!importPreviewRows.length || isProcessingImport}
                    onClick={handleProcesarImportacionClientes}
                    startIcon={isProcessingImport ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                    sx={{ fontWeight: 700 }}
                  >
                    {isProcessingImport
                      ? (importProgress ? `Procesando ${importProgress.procesados} / ${importProgress.total}` : 'Procesando...')
                      : 'Importar'}
                  </Button>
                </Box>
              )}
              {importResult && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Resultados de la Importación
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: importResult.conErrores > 0 ? '#ffebee' : '#e8f5e9',
                      border: '1px solid',
                      borderColor: importResult.conErrores > 0 ? '#ffcdd2' : '#c8e6c9',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Total procesados:</strong> {importResult.total}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Exitosos:</strong> {importResult.exitosos}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Con errores:</strong> {importResult.conErrores}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tasa de éxito:</strong>{' '}
                      {importResult.total > 0
                        ? `${Math.round((importResult.exitosos / importResult.total) * 100)}%`
                        : '0%'}
                    </Typography>
                  </Box>
                  {importResult.mensajesError.length > 0 && (
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #ffe0b2',
                        bgcolor: '#fff3e0',
                        p: 2,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Detalles de errores:
                      </Typography>
                      {importResult.mensajesError.map((m, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{ mb: 0.5, whiteSpace: 'pre-line' }}
                        >
                          {m}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {importResult ? (
            <Button onClick={() => setOpenImportar(false)} variant="contained" color="primary">
              Finalizar
            </Button>
          ) : (
            <Button onClick={() => setOpenImportar(false)} color="inherit">
              Cancelar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clientes;
